<template>
  <ScioModal
    :open="true"
    :title="header || 'Confirm'"
    :description="information"
    size="sm"
    :body-class="hasBody ? '' : 'hidden'"
    :initial-focus="inputConfig?.showInput ? 'input' : ''"
    :panel-class="panelClass"
    :header-class="headerClass"
    :footer-class="footerClass"
    @close="emitCancel"
  >
    <template v-if="icon" #icon>
      <img :src="icon" alt="" aria-hidden="true" class="h-5 w-5" />
    </template>

    <div v-if="inputConfig?.showInput" class="space-y-2">
      <label class="scio-text-meta scio-weight-semibold text-slate-700">
        {{ inputConfig.inputLabel || "Input" }}
      </label>
      <input
        v-model="localInput"
        type="text"
        class="scio-input w-full"
        :placeholder="inputConfig.placeholder || ''"
        @keydown.enter.prevent="emitDefaultAction"
      />
      <p v-if="inputConfig.helpText" class="scio-text-meta text-slate-500">
        {{ inputConfig.helpText }}
      </p>
    </div>

    <slot />

    <template v-if="hasActions" #footer>
      <button
        v-if="buttonConfig?.cancel && !hasCustomCancel"
        type="button"
        class="scio-btn-secondary"
        @click="emitCancel"
      >
        Cancel
      </button>

      <button
        v-if="buttonConfig?.remove"
        type="button"
        class="scio-btn-danger"
        @click="emitButton('remove')"
      >
        Remove
      </button>

      <button
        v-for="button in orderedCustomButtons"
        :key="button.value"
        type="button"
        :class="buttonClass(button)"
        :disabled="button.disabled"
        @click="emitButton(button.value)"
      >
        <img
          v-if="button.icon"
          :src="button.icon"
          alt=""
          aria-hidden="true"
          class="h-4 w-4"
        />
        {{ button.text }}
      </button>
    </template>
  </ScioModal>
</template>

<script>
import ScioModal from "./ScioModal.vue";

const DESTRUCTIVE_ACTIONS = new Set(["delete", "remove", "logout"]);
const SECONDARY_ACTIONS = new Set(["cancel", "close", "stay"]);

export default {
  name: "ModalBox",
  components: { ScioModal },

  props: {
    header: { type: String, default: "" },
    information: { type: String, default: "" },
    icon: { type: String, default: "" },
    buttonConfig: { type: Object, default: () => ({}) },
    inputConfig: { type: Object, default: () => ({}) },
    panelClass: { type: [String, Array, Object], default: "rounded-none" },
    headerClass: { type: [String, Array, Object], default: "" },
    footerClass: { type: [String, Array, Object], default: "bg-white" },
  },

  emits: ["modal-click"],

  data() {
    return {
      localInput: this.inputConfig?.defaultValue ?? "",
    };
  },

  computed: {
    hasBody() {
      return Boolean(this.inputConfig?.showInput || this.$slots.default);
    },
    hasActions() {
      return Boolean(
        this.buttonConfig?.cancel
        || this.buttonConfig?.remove
        || this.buttonConfig?.customButtons?.length,
      );
    },
    hasCustomCancel() {
      return this.buttonConfig?.customButtons?.some((button) => button.value === "cancel");
    },
    orderedCustomButtons() {
      return [...(this.buttonConfig?.customButtons || [])].sort((left, right) => {
        const leftPriority = SECONDARY_ACTIONS.has(left.value) ? 0 : 1;
        const rightPriority = SECONDARY_ACTIONS.has(right.value) ? 0 : 1;
        return leftPriority - rightPriority;
      });
    },
  },

  watch: {
    inputConfig: {
      immediate: true,
      deep: true,
      handler(value) {
        this.localInput = value?.defaultValue ?? "";
      },
    },
  },

  methods: {
    emitButton(buttonType) {
      this.$emit("modal-click", { buttonType, inputValue: this.localInput });
    },
    emitCancel() {
      this.emitButton("cancel");
    },
    emitDefaultAction() {
      const action = (this.buttonConfig?.customButtons || []).find(
        (button) => !SECONDARY_ACTIONS.has(button.value) && !button.disabled,
      );
      if (action) this.emitButton(action.value);
    },
    buttonClass(button) {
      if (SECONDARY_ACTIONS.has(button.value)) return "scio-btn-secondary";
      if (DESTRUCTIVE_ACTIONS.has(button.value)) return "scio-btn-danger";
      return "scio-btn-primary";
    },
  },
};
</script>
